import type { ComponentType } from 'react'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween.js'

import { useVariants, VariantComponentMap, VariantPredicateList } from './useVariants.js'

dayjs.extend(isBetween)

type FestiveVariant = 'default' | 'sinterklaas' | 'christmas' | 'easter'

// Based on https://en.wikipedia.org/wiki/Date_of_Easter#Meeus's_Julian_algorithm
// and https://stackoverflow.com/a/44480326
const getEasterDate = (year: number): Date => {
  const date = new Date()

  // Set the timestamp to midnight and set the year.
  date.setHours(0, 0, 0, 0)
  date.setFullYear(year)

  // Find the golden number.
  const a = year % 19

  // Choose which version of the algorithm to use based on the given year.
  const b = year >= 2200 && year <= 2299 ? (11 * a + 4) % 30 : (11 * a + 5) % 30

  // Determine whether or not to compensate for the previous step.
  const c = b === 0 || (b === 1 && a > 10) ? b + 1 : b

  // Use c first to find the month: April or March.
  const m = c >= 1 && c <= 19 ? 3 : 2

  // Then use c to find the full moon after the northward equinox.
  const d = (50 - c) % 31

  // Mark the date of that full moon — the "Paschal" full moon.
  date.setMonth(m, d)

  // Count forward the number of days until the following Sunday (Easter).
  date.setMonth(m, d + (7 - date.getDay()))

  return date
}

export const isSinterklaas = (now: Date): boolean => {
  const today = dayjs(now)
  const saturdayDayNum = 6

  // Sinterklaas intocht is the first Saturday between November 12th and 18th
  const start = new Date(today.year(), 10, 12)
  const daysToAdd = saturdayDayNum - start.getDay()
  const sinterklaasSaturday = dayjs(start).add(daysToAdd, 'd')
  const endDate = new Date(today.year(), 11, 5)

  return today.isBetween(sinterklaasSaturday, endDate, null, '[]')
}

export const isChristmas = (now: Date): boolean => {
  const today = dayjs(now)
  const startDate = new Date(today.year(), 11, 6)
  const endDate = new Date(today.year(), 11, 26)

  return today.isBetween(startDate, endDate, null, '[]')
}

export const isEaster = (now: Date): boolean => {
  const today = dayjs(now)
  const easterDate = dayjs(getEasterDate(today.year()))
  const startDate = easterDate.subtract(1, 'week')
  const endDate = easterDate.add(1, 'day')

  return today.isBetween(startDate, endDate, null, '[]')
}

const topLevelDomain = (hostname = window.location.hostname) => {
  const host = hostname.toLowerCase()
  return host.slice(host.lastIndexOf('.') + 1)
}

const isTopLevelDomain = (tlds: string[], hostname = window.location.hostname): boolean => {
  const activeTld = topLevelDomain(hostname)
  return tlds.some((tld) => tld === activeTld)
}

const isLocalHost = (hostname = window.location.hostname): boolean => {
  const host = hostname.toLowerCase()
  return host === 'localhost' || host === '127.0.0.1'
}

const variants: VariantPredicateList<FestiveVariant, Date> = [
  ['easter', (now) => (isLocalHost() || isTopLevelDomain(['be', 'nl', 'se'])) && isEaster(now)],
  ['sinterklaas', (now) => (isLocalHost() || isTopLevelDomain(['nl'])) && isSinterklaas(now)],
  ['christmas', isChristmas],
  ['default', () => true],
]

export const useFestiveVariants = <TComponent extends {}>(
  map: VariantComponentMap<TComponent, FestiveVariant>,
  now: Date = new Date()
): ComponentType<TComponent> => useVariants<TComponent, Date>(variants, map, now)
